import React, { Dispatch, SetStateAction } from 'react';
import { Grid, TextField, Typography } from '@mui/material';
import { Drawer, closeDrawer } from '@innovyze/stylovyze';
import { RehabAction } from '@Types';
import { useDispatch } from 'react-redux';
import { useGlobalization } from '@Translations';
import { useRehabActionForm } from './useRehabActionForm';
import { CostSourceRadio } from './CostSourceRadio';
import { CustomCostFields } from './CustomCostFields';
import { CostTableFields } from './CostTableFields';

interface Props {
	treeId: string;
	viewerAccess: boolean;
	rehabAction: RehabAction | undefined | null;
	shouldAddNewActionToTree: () => boolean;
	setIsDirty: Dispatch<SetStateAction<boolean>>;
	trenchCalculationEnabled: boolean;
}

export const RehabActionDrawerV2 = ({
	treeId,
	viewerAccess,
	rehabAction,
	shouldAddNewActionToTree,
	setIsDirty,
	trenchCalculationEnabled,
}: Props) => {
	const { t } = useGlobalization();
	const dispatch = useDispatch();

	const form = useRehabActionForm({
		treeId,
		rehabAction,
		shouldAddNewActionToTree,
		setIsDirty,
	});

	const noCostTablesHint =
		form.costTables.length === 0
			? t('No cost tables available for this asset type')
			: undefined;

	return (
		<Drawer
			action={{
				'data-cy': 'drawer-action',
				label: rehabAction ? t('Save') : t('Add'),
				onClick: () => {
					if (form.handleAddOrSave()) {
						dispatch(closeDrawer());
					}
				},
				disabled: form.isSaveDisabled,
			}}
			drawerId="addNewActionDrawer"
			onCancel={() => dispatch(closeDrawer())}
			onOpen={() => form.resetFromAction(rehabAction)}
			title={
				rehabAction ? t('Edit Rehab Action') : t('New Rehab Action')
			}>
			<form noValidate autoComplete="off">
				<Grid container spacing={2}>
					<Grid xs={12} item>
						<Typography variant="body2" color="textSecondary">
							{t(
								'Define what happens to assets that reach this point in the tree. Choose a name, then set how the cost is calculated.',
							)}
						</Typography>
					</Grid>
					<Grid xs={12} item>
						<TextField
							fullWidth
							required
							name="Name"
							inputProps={{ 'data-cy': 'action-name-input' }}
							onChange={form.onNameChange}
							value={form.name}
							label={t('Name')}
							variant="outlined"
							helperText={form.validName}
							error={form.validName !== ''}
						/>
					</Grid>
					<Grid xs={12} item>
						<CostSourceRadio
							costSource={form.costSource}
							onChange={form.setCostSource}
							costTablesAvailable={form.costTables.length > 0}
							noCostTablesHint={noCostTablesHint}
						/>
					</Grid>
					{form.costSource === 'custom' ? (
						<CustomCostFields
							initialUnitCost={form.initialUnitCost}
							costUnit={form.costUnit}
							validUnitCost={form.validUnitCost}
							viewerAccess={viewerAccess}
							hasLengthProperty={form.hasLengthProperty}
							system={form.system}
							onUnitCostChange={form.onUnitCostChange}
							trenchCalculationEnabled={trenchCalculationEnabled}
							selectedCostApplication={form.costApplication}
							onCostPerChange={form.onCostPerChange}
							defectCodes={form.defectCodes}
							defectCodeOptions={form.defectCodeOptions}
							defectCodesLoading={form.defectCodesLoading}
							onDefectCodesChange={form.setDefectCodes}
						/>
					) : (
						<CostTableFields
							costTables={form.costTables}
							selectedCostTableId={form.selectedCostTableId}
							onSelect={form.setSelectedCostTableId}
							summary={form.costTableSummary}
							trenchCalculationEnabled={trenchCalculationEnabled}
							system={form.system as 'Metric' | 'Imperial'}
							selectedCostApplication={form.costApplication}
							onSelectCostApplication={form.setCostApplication}
							defectCodes={form.defectCodes}
							defectCodeOptions={form.defectCodeOptions}
							defectCodesLoading={form.defectCodesLoading}
							onDefectCodesChange={form.setDefectCodes}
						/>
					)}
					<Grid xs={12} item>
						<TextField
							fullWidth
							multiline
							name="Notes"
							placeholder={t('Add notes (optional)')}
							onChange={form.onNotesChange}
							value={form.notes}
							label={t('Notes')}
							variant="outlined"
						/>
					</Grid>
				</Grid>
			</form>
		</Drawer>
	);
};
